"use client";

import { LanguageChoices } from "@/components/language-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { TrackedSignupLink } from "@/components/tracked-signup-link";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowUpRightIcon,
  BanknoteIcon,
  BlocksIcon,
  HeartHandshakeIcon,
  HomeIcon,
  type LucideIcon,
  MenuIcon,
  NewspaperIcon,
  UsersIcon,
} from "lucide-react";
import { UnreadDot } from "@/components/unread-dot";
import { useBlogUnread, type BlogUnread } from "@/components/blog-unread-dot";
import { LocalizedLink as Link } from "@/components/localized-link";
import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

interface NavLink {
  href: string;
  /** Lingui descriptor — serializable across the server boundary; rendered
   *  with the client i18n here. */
  label: MessageDescriptor;
  external?: boolean;
}

interface Props {
  currentPath: string;
  isSignedIn: boolean;
  appHref: string;
  navLinks: readonly NavLink[];
  /** What Blog's new-post dot compares (components/blog-unread-dot.tsx). */
  blogUnread: BlogUnread;
  /** "New post", already in the reader's language. */
  newPostLabel: string;
}

const iconForHref: Record<string, LucideIcon> = {
  "/": HomeIcon,
  "/integrations": BlocksIcon,
  "/customers": HeartHandshakeIcon,
  "/blog": NewspaperIcon,
  "/about": UsersIcon,
  "/pricing": BanknoteIcon,
};

export function MobileMenu({ currentPath, isSignedIn, appHref, navLinks, blogUnread, newPostLabel }: Props) {
  const [open, setOpen] = useState(false);
  const { i18n, t } = useLingui();
  // On a phone the links are behind the button, so the button carries the dot too.
  const unreadPost = useBlogUnread(blogUnread);

  const close = () => { setOpen(false); };

  const allLinks: readonly NavLink[] = [
    { href: "/", label: msg`Home` },
    ...navLinks,
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger
        aria-label={t`Open menu`}
        // `lg:hidden`, matching SiteNav's inline links: the tablet band gets the
        // menu, because the inline row does not fit there in every language.
        className="relative inline-flex size-10 items-center justify-center text-foreground-strong transition hover:text-signal lg:hidden"
      >
        <MenuIcon className="size-5" />
        {unreadPost && <UnreadDot label={newPostLabel} className="absolute right-1.5 top-1.5" />}
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-3/4 max-w-xs bg-background px-6 pb-8 pt-6 sm:max-w-xs"
      >
        <SheetTitle className="sr-only"><Trans>Menu</Trans></SheetTitle>
        <SheetDescription className="sr-only">
          <Trans>Site navigation and account actions</Trans>
        </SheetDescription>

        <nav className="mt-10 flex flex-col">
          {allLinks.map((link) => {
            const isActive = currentPath === link.href;
            const Icon = iconForHref[link.href];
            const linkClassName =
              "flex items-center gap-3 px-2 py-3 text-base font-medium text-foreground-muted transition-colors hover:text-foreground aria-[current=page]:text-foreground-strong";
            const inner = (
              <>
                {Icon ? (
                  <Icon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-foreground-muted/80"
                  />
                ) : null}
                <span>{i18n._(link.label)}</span>
                {link.href === "/blog" && unreadPost && <UnreadDot label={newPostLabel} />}
                {link.external ? (
                  <ArrowUpRightIcon
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-foreground-muted/60"
                  />
                ) : null}
              </>
            );

            if (link.external) {
              return (
                <SheetClose asChild key={link.href}>
                  <a
                    href={link.href}
                    onClick={close}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    {inner}
                  </a>
                </SheetClose>
              );
            }

            return (
              <SheetClose asChild key={link.href}>
                <Link
                  href={link.href}
                  aria-current={isActive ? "page" : undefined}
                  onClick={close}
                  className={linkClassName}
                >
                  {inner}
                </Link>
              </SheetClose>
            );
          })}
        </nav>

        <div className="mt-4">
          {isSignedIn ? (
            <SheetClose asChild>
              <Link
                href={appHref}
                onClick={close}
                className="flex w-full items-center justify-center bg-signal px-5 py-3 text-base font-medium text-cream transition hover:bg-signal-deep"
              >
                <Trans>Open Chat</Trans>
              </Link>
            </SheetClose>
          ) : (
            <div className="space-y-2">
              {/* Prominent sign-up CTA, quieter Login below — the standard header
                  pattern, stacked for the mobile sheet. */}
              <SheetClose asChild>
                <TrackedSignupLink
                  href="/get-started?returnTo=/api/signup-redirect"
                  className="flex w-full items-center justify-center bg-signal px-5 py-3 text-base font-medium text-cream transition hover:bg-signal-deep"
                  intent="signup"
                  source="nav-mobile"
                >
                  <Trans>Sign up</Trans>
                </TrackedSignupLink>
              </SheetClose>
              <SheetClose asChild>
                <TrackedSignupLink
                  href="/auth/login?returnTo=/api/signup-redirect"
                  className="flex w-full items-center justify-center border border-border-subtle px-5 py-3 text-base font-medium text-foreground-muted transition hover:border-border-strong hover:text-foreground"
                  intent="login"
                  source="nav-mobile"
                >
                  <Trans>Login</Trans>
                </TrackedSignupLink>
              </SheetClose>
            </div>
          )}
        </div>

        {/* Theme lives here rather than in the nav bar so the header stays quiet;
            signed-in users also have it in the profile menu. */}
        <div className="mt-6 flex items-center justify-between border-t border-border-subtle px-2 pt-5">
          <span className="text-base font-medium text-foreground-muted"><Trans>Theme</Trans></span>
          <ThemeToggle />
        </div>

        {/* The phone-width half of the header language control (the bar's copy
            is desktop-only). It has to be reachable from every public page, not
            just the tall ones: the automatic redirect can land someone on a
            language they cannot read, and scrolling to a footer is a poor way
            to discover the way out. */}
        <div className="mt-5 border-t border-border-subtle pt-5">
          {/* Same label treatment as the Theme row above — they sit together at
              the foot of the sheet and should read as siblings. */}
          <span className="px-2 text-base font-medium text-foreground-muted"><Trans>Language</Trans></span>
          <div className="mt-1">
            <LanguageChoices />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
